import axios from "axios";
import {
  GET_JOB_PROFILE_DETAILS_URL,
  LIST_JOB_ADVERTS_URL,
  UPDATE_ADVERT_URL,
} from "@/utils/constants/constants.js";

function padDateTimeSegment(value) {
  return String(value).padStart(2, "0");
}

function emptyAdvertTable() {
  return { headers: [], rows: [] };
}

function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export default {
  name: "EditAdvertModal",
  props: {
    headers: { type: Object, required: true },
    selectedAdvert: { type: Object, default: null },
    openNonce: { type: Number, default: 0 },
  },
  emits: ["advert-updated", "advert-error"],
  data() {
    return {
      dragDisplayIndex: null,
      activeRichEditor: null,
      advertSaving: false,
      editAdvertData: {
        id: null,
        title: "",
        reference_number: "",
        published_date: "",
        closing_date: "",
        vacancies: null,
        details: "",
        sections: [],
        job_profile_title: "",
        job_profile_description: "",
        job_profile_sections: [],
        display_order: [],
      },
    };
  },
  watch: {
    openNonce() {
      this.initializeEditAdvertModal();
    },
  },
  computed: {
    editAdvertMinimumDateTime() {
      const now = new Date();
      return `${now.getFullYear()}-${padDateTimeSegment(now.getMonth() + 1)}-${padDateTimeSegment(now.getDate())}T${padDateTimeSegment(now.getHours())}:${padDateTimeSegment(now.getMinutes())}`;
    },
    editAdvertClosingMinimumDateTime() {
      return this.editAdvertData.published_date || this.editAdvertMinimumDateTime;
    },
  },
  methods: {
    getNormalizedAdvertTable(table) {
      if (!table || typeof table !== "object") {
        return emptyAdvertTable();
      }
      table.headers = ensureArray(table.headers);
      table.rows = ensureArray(table.rows);
      return table;
    },
    syncAdvertTableRowsToHeaders(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      const columnCount = normalizedTable.headers.length;

      normalizedTable.rows = normalizedTable.rows.map((row) => {
        const normalizedRow = Array.isArray(row) ? [...row] : [];
        if (normalizedRow.length > columnCount) {
          normalizedRow.length = columnCount;
        }
        while (normalizedRow.length < columnCount) {
          normalizedRow.push("");
        }
        return normalizedRow;
      });

      return normalizedTable;
    },
    addAdvertTableHeader(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      normalizedTable.headers.push("");
      this.syncAdvertTableRowsToHeaders(normalizedTable);
    },
    removeAdvertTableHeader(table, headerIndex) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      if (headerIndex < 0 || headerIndex >= normalizedTable.headers.length) return;

      normalizedTable.headers.splice(headerIndex, 1);
      normalizedTable.rows = normalizedTable.rows.map((row) => {
        if (!Array.isArray(row)) return [];
        const nextRow = [...row];
        nextRow.splice(headerIndex, 1);
        return nextRow;
      });
    },
    addAdvertTableRow(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      const columnCount = normalizedTable.headers.length || 1;
      normalizedTable.rows.push(new Array(columnCount).fill(""));
      this.syncAdvertTableRowsToHeaders(normalizedTable);
    },
    buildEmptyAdvertSubsection() {
      return {
        title: "",
        type: "text",
        content: "",
        table: { headers: [], rows: [] },
      };
    },
    buildEmptyAdvertSection() {
      return {
        title: "",
        type: "text",
        content: "",
        table: { headers: [], rows: [] },
        subsections: [],
      };
    },
    sectionHasSubsections(section) {
      return Array.isArray(section?.subsections) && section.subsections.length > 0;
    },
    clearSectionContentForSubsections(section) {
      if (!section) return;
      section.type = "text";
      section.content = "";
      section.table = emptyAdvertTable();
    },
    buildNormalizedSubsectionContent(subsection) {
      return subsection?.content || "";
    },
    buildNormalizedSubsectionTable(subsection) {
      return subsection?.table || emptyAdvertTable();
    },
    normalizeAdvertSubsection(subsection) {
      return {
        title: subsection?.title || "",
        type: subsection?.type || "text",
        content: this.buildNormalizedSubsectionContent(subsection),
        table: this.buildNormalizedSubsectionTable(subsection),
      };
    },
    buildNormalizedSectionSubsections(section) {
      if (!Array.isArray(section?.subsections)) return [];
      return section.subsections.map((subsection) => this.normalizeAdvertSubsection(subsection));
    },
    buildNormalizedSectionTitle(section) {
      return section?.title || "";
    },
    buildNormalizedSectionType(section) {
      return section?.type || "text";
    },
    buildNormalizedSectionContent(section) {
      return section?.content || "";
    },
    buildNormalizedSectionTable(section) {
      return section?.table || emptyAdvertTable();
    },
    buildNormalizedSectionBase(section) {
      return {
        title: this.buildNormalizedSectionTitle(section),
        type: this.buildNormalizedSectionType(section),
        content: this.buildNormalizedSectionContent(section),
        table: this.buildNormalizedSectionTable(section),
        subsections: this.buildNormalizedSectionSubsections(section),
      };
    },
    normalizeAdvertSection(section) {
      const normalized = this.buildNormalizedSectionBase(section);
      if (this.sectionHasSubsections(normalized)) {
        this.clearSectionContentForSubsections(normalized);
      }
      return normalized;
    },
    splitAdvertSectionsAndOrder(rawSections) {
      const list = Array.isArray(rawSections) ? JSON.parse(JSON.stringify(rawSections)) : [];
      const meta = list.find(item => item && item.type === "__display_order_meta__" && Array.isArray(item.items));
      return {
        contentSections: list.filter(item => !(item && item.type === "__display_order_meta__")),
        orderItems: meta?.items || [],
      };
    },
    normalizeDisplayOrder(savedOrder, hasDetails, sections) {
      const fallback = [];
      if (hasDetails) fallback.push({ key: "details", source: "details" });
      (sections || []).forEach((_, idx) => fallback.push({ key: `section:${idx}`, source: "section", index: idx }));

      const allowed = new Set(fallback.map(item => item.key));
      const normalized = [];
      (Array.isArray(savedOrder) ? savedOrder : []).forEach(item => {
        if (item && allowed.has(item.key) && !normalized.some(entry => entry.key === item.key)) {
          normalized.push(item);
        }
      });
      fallback.forEach(item => {
        if (!normalized.some(entry => entry.key === item.key)) {
          normalized.push(item);
        }
      });
      return normalized;
    },
    _orDefault(val, fallback) {
      return val || fallback;
    },
    _format_datetime_local(val) {
      if (!val) return '';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '';
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    _format_date_input(val) {
      if (!val) return '';
      const date = new Date(val);
      if (isNaN(date.getTime())) return '';
      const pad = num => String(num).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    parseAdvertDateTime(value) {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    buildAdvertDateRangeError(opening, closing) {
      if (!opening || !closing) {
        return "Please enter valid opening and closing date & time values.";
      }
      if (closing <= opening) {
        return "Closing date & time must be later than opening date & time.";
      }
      return "";
    },
    validateAdvertDateRange(openingDate, closingDate) {
      if (!openingDate || !closingDate) return true;
      const opening = this.parseAdvertDateTime(openingDate);
      const closing = this.parseAdvertDateTime(closingDate);
      const errorMessage = this.buildAdvertDateRangeError(opening, closing);
      if (!errorMessage) return true;
      this.$emit("advert-error", errorMessage);
      return false;
    },
    buildEmptyEditData() {
      return { id: null, title: "", reference_number: "", published_date: "", closing_date: "", vacancies: null, details: "", sections: [] };
    },
    buildEditDataFromAdvert(advert, splitResult) {
      return {
        id: advert.id,
        title: this._orDefault(advert.title, ""),
        reference_number: this._orDefault(advert.reference_number, ""),
        published_date: this._format_date_input(advert.published_date),
        closing_date: this._format_datetime_local(advert.closing_date),
        vacancies: this._orDefault(advert.vacancies, null),
        details: this._orDefault(advert.details, ""),
        sections: (splitResult.contentSections || []).map((section) => this.normalizeAdvertSection(section)),
        job_profile_title: "",
        job_profile_description: "",
        job_profile_sections: [],
        display_order: [],
      };
    },
    extractPayload(response) {
      return response.data?.data || response.data || {};
    },
    filterAdvertSections(sections) {
      return (Array.isArray(sections) ? sections : []).filter(section => section.show_on_advert !== false);
    },
    getProfileTitle(payload) {
      return payload.job_title || payload.title || "";
    },
    hasNonEmptyDetails() {
      return !!(this.editAdvertData.details && String(this.editAdvertData.details).trim());
    },
    applyEditProfileData(response, splitResult) {
      const payload = this.extractPayload(response);
      const profileSections = this.filterAdvertSections(payload.sections);
      const hasDetails = this.hasNonEmptyDetails();
      this.editAdvertData.job_profile_title = this.getProfileTitle(payload);
      this.editAdvertData.job_profile_description = this._orDefault(payload.description, "");
      this.editAdvertData.job_profile_sections = profileSections;
      this.editAdvertData.display_order = this.normalizeDisplayOrder(splitResult.orderItems, hasDetails, this.editAdvertData.sections);
    },
    async initializeEditAdvertModal() {
      const advert = await this.fetchLatestAdvertForEdit();
      if (!advert?.id) {
        this.editAdvertData = this.buildEmptyEditData();
        return;
      }

      const split = this.splitAdvertSectionsAndOrder(advert.sections || []);
      this.editAdvertData = this.buildEditDataFromAdvert(advert, split);

      await axios.get(GET_JOB_PROFILE_DETAILS_URL, {
        headers: this.headers,
        withCredentials: true,
        params: { entity_type_id: advert.entity_type_id },
      }).then(response => {
        this.applyEditProfileData(response, split);
      }).catch(error => {
        this.$emit("advert-error", error.response?.data?.message || error.message || "Failed to load advert profile details.");
      });
    },
    async fetchLatestAdvertForEdit() {
      const selectedAdvert = this.selectedAdvert;
      const advertId = Number(selectedAdvert?.id);
      const entityTypeId = Number(selectedAdvert?.entity_type_id);

      if (!this.canFetchLatestAdvert(advertId, entityTypeId)) return selectedAdvert;

      try {
        const response = await axios.get(LIST_JOB_ADVERTS_URL, {
          headers: this.headers,
          withCredentials: true,
          params: { entity_type_id: entityTypeId },
        });
        return this.mergeLatestAdvert(selectedAdvert, response, advertId);
      } catch (_) {
        return selectedAdvert;
      }
    },
    canFetchLatestAdvert(advertId, entityTypeId) {
      return !!advertId && !!entityTypeId;
    },
    mergeLatestAdvert(selectedAdvert, response, advertId) {
      const rows = response?.data?.data || [];
      const latestAdvert = (Array.isArray(rows) ? rows : []).find((row) => Number(row?.id) === advertId);
      return latestAdvert ? { ...selectedAdvert, ...latestAdvert } : selectedAdvert;
    },
    advertCleanHtml(value) {
      if (!value) return "";
      const str = String(value);
      if (/<[a-z][\s\S]*>/i.test(str)) return str;
      return str.split('\n').join('<br>');
    },
    advertStripHtmlTags(text) {
      if (!text) return "";
      return String(text).replace(/<[^>]+>/g, "").trim();
    },
    advertHasTableContent(section) {
      if (!section?.table || !Array.isArray(section.table.rows)) return false;

      for (const row of section.table.rows) {
        if (!Array.isArray(row)) continue;
        for (const cell of row) {
          if (this.advertStripHtmlTags(cell || "")) {
            return true;
          }
        }
      }

      return false;
    },
    rebuildEditDisplayOrder() {
      const hasDetails = !!(this.editAdvertData.details && String(this.editAdvertData.details).trim());
      const sections = this.editAdvertData.sections || [];
      const fallback = [];

      if (hasDetails) {
        fallback.push({ key: "details", source: "details" });
      }

      sections.forEach((_, idx) => {
        fallback.push({ key: `section:${idx}`, source: "section", index: idx });
      });

      const allowed = new Set(fallback.map(item => item.key));
      const normalized = [];
      const current = Array.isArray(this.editAdvertData.display_order) ? this.editAdvertData.display_order : [];

      current.forEach(item => {
        if (item && allowed.has(item.key) && !normalized.some(entry => entry.key === item.key)) {
          normalized.push(item);
        }
      });

      fallback.forEach(item => {
        if (!normalized.some(entry => entry.key === item.key)) {
          normalized.push(item);
        }
      });

      this.editAdvertData.display_order = normalized;
    },
    addAdvertSection() {
      if (!Array.isArray(this.editAdvertData.sections)) {
        this.editAdvertData.sections = [];
      }

      this.editAdvertData.sections.push(this.buildEmptyAdvertSection());

      this.rebuildEditDisplayOrder();
    },
    getEditAdvertSection(sectionIndex) {
      const sections = this.editAdvertData.sections;
      if (!this.isValidEditAdvertSectionIndex(sections, sectionIndex)) return null;
      return sections.find((_, index) => index === sectionIndex) || null;
    },
    isValidEditAdvertSectionIndex(sections, sectionIndex) {
      if (!Array.isArray(sections) || !Number.isInteger(sectionIndex)) return false;
      return sectionIndex >= 0 && sectionIndex < sections.length;
    },
    addAdvertSubsection(sectionIndex) {
      const section = this.getEditAdvertSection(sectionIndex);
      if (!section) return;
      if (!Array.isArray(section.subsections)) {
        section.subsections = [];
      }
      this.clearSectionContentForSubsections(section);
      section.subsections.push(this.buildEmptyAdvertSubsection());
    },
    removeAdvertSubsection(sectionIndex, subsectionIndex) {
      const section = this.getEditAdvertSection(sectionIndex);
      if (!Array.isArray(section?.subsections)) return;
      if (subsectionIndex < 0 || subsectionIndex >= section.subsections.length) return;
      section.subsections.splice(subsectionIndex, 1);
    },
    removeAdvertSection(index) {
      if (!Array.isArray(this.editAdvertData.sections)) return;
      if (index < 0 || index >= this.editAdvertData.sections.length) return;
      this.editAdvertData.sections.splice(index, 1);
      this.rebuildEditDisplayOrder();
    },
    onDisplayDragStart(index) {
      this.dragDisplayIndex = index;
    },
    onDisplayDragEnd() {
      this.dragDisplayIndex = null;
    },
    onDisplayDrop(targetIndex) {
      if (!Array.isArray(this.editAdvertData.display_order)) return;
      const sourceIndex = this.dragDisplayIndex;
      if (sourceIndex === null || sourceIndex === undefined || sourceIndex === targetIndex) {
        this.dragDisplayIndex = null;
        return;
      }

      const list = [...this.editAdvertData.display_order];
      const [moved] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, moved);
      this.editAdvertData.display_order = list;
      this.dragDisplayIndex = null;
    },
    normalizeRichEditor(editor) {
      if (!editor) return;
      editor.setAttribute("dir", "ltr");
      editor.style.setProperty("direction", "ltr", "important");
      editor.style.setProperty("unicode-bidi", "isolate", "important");
      editor.style.setProperty("writing-mode", "horizontal-tb", "important");
      editor.style.setProperty("text-align", "left", "important");
    },
    setActiveRichEditor(event) {
      this.activeRichEditor = event?.target || null;
      this.normalizeRichEditor(this.activeRichEditor);
    },
    applyRichTextCommand(command, value = null) {
      if (!this.activeRichEditor) return;
      this.normalizeRichEditor(this.activeRichEditor);
      this.activeRichEditor.focus();
      document.execCommand(command, false, value);
      this.normalizeRichEditor(this.activeRichEditor);
      this.activeRichEditor.dispatchEvent(new Event("blur", { bubbles: true }));
    },
    buildUpdateAdvertPayload() {
      const data = this.editAdvertData;
      return {
        advert_id: data.id,
        title: data.title.trim(),
        reference_number: this._orDefault(data.reference_number, null),
        published_date: this._orDefault(data.published_date, null),
        closing_date: this._orDefault(data.closing_date, null),
        vacancies: this._orDefault(data.vacancies, null),
        details: this._orDefault(data.details, null),
        sections: this.buildEditSectionsPayload(),
      };
    },
    buildEditSectionsPayload() {
      const data = this.editAdvertData;
      return [
        ...(data.sections || []),
        { type: "__display_order_meta__", items: data.display_order || [] },
      ];
    },
    handleUpdateAdvertSuccess() {
      const closeButton = document.querySelector("#edit_advert_modal .modal-btn-close");
      if (closeButton) closeButton.click();
      this.$emit("advert-updated");
    },
    async emitUpdateAdvert() {
      if (this.activeRichEditor) {
        this.activeRichEditor.dispatchEvent(new Event("blur", { bubbles: true }));
      }
      this.rebuildEditDisplayOrder();

      if (!this.editAdvertData.title || !this.editAdvertData.title.trim()) {
        this.$emit("advert-error", "Advert title is required.");
        return;
      }
      if (!this.editAdvertData.reference_number || !String(this.editAdvertData.reference_number).trim()) {
        this.$emit("advert-error", "Reference number is required.");
        return;
      }

      if (!this.validateAdvertDateRange(this.editAdvertData.published_date, this.editAdvertData.closing_date)) {
        return;
      }

      this.advertSaving = true;
      await axios.post(UPDATE_ADVERT_URL, this.buildUpdateAdvertPayload(), {
        headers: this.headers,
        withCredentials: true,
      }).then(response => {
        if (response.data?.status !== "success") {
          throw new Error(response.data?.message || "Unexpected response");
        }
        this.handleUpdateAdvertSuccess();
      }).catch(error => {
        this.$emit("advert-error", error.response?.data?.message || error.message || "Failed to update advert.");
      }).finally(() => {
        this.advertSaving = false;
      });
    },
  },
};
